import React, { useState, useEffect, useContext } from 'react';
import { API, AuthContext } from '@/App';
import { Link } from 'react-router-dom';
import {
  CheckCircle,
  ListChecks,
  Users,
  ClipboardCheck,
  Shield,
  FileCheck,
  Activity,
  Calendar
} from 'lucide-react';

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState({ stores: 0, team_members: 0, active_stocktakes: 0, devices: 0 });
  const [countdown, setCountdown] = useState({ days: '-', hours: '-', mins: '-', secs: '-' });
  const [stocktakes, setStocktakes] = useState([]);
  const [stores, setStores] = useState([]);
  const [currentStore, setCurrentStore] = useState(null);
  const [checklistProgress, setChecklistProgress] = useState({ completed: 0, total: 31 });
  const token = localStorage.getItem('token');

  // Function to fetch data for a specific store
  const fetchDashboardData = (storeId) => {
    const storeParam = storeId ? `?store_id=${storeId}` : '';
    
    // Fetch dashboard stats for the store
    fetch(`${API}/dashboard/stats${storeParam}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => setStats(data))
      .catch(console.error);

    // Fetch stocktakes for the store
    fetch(`${API}/stocktakes${storeParam}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        const sortedStocktakes = data.sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date));
        setStocktakes(sortedStocktakes);
      })
      .catch(console.error);
  };

  useEffect(() => {
    // Fetch stores first to determine current store
    fetch(`${API}/stores`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        setStores(data);
        
        let selectedStore = null;
        // For Network Admin or Regional Manager, check if they have a selected store
        if (user?.role === 'network_admin' || user?.role === 'regional_manager') {
          const savedStoreId = localStorage.getItem('selectedStoreId');
          if (savedStoreId && data.find(s => s.id === savedStoreId)) {
            selectedStore = data.find(s => s.id === savedStoreId);
          } else if (data.length > 0) {
            selectedStore = data[0];
          }
        } else {
          // For other users, find their assigned store or default to first store
          selectedStore = data.find(s => s.id === user?.store_id) || data[0];
        }
        
        setCurrentStore(selectedStore);
        // Fetch data for the selected store
        if (selectedStore) {
          fetchDashboardData(selectedStore.id);
        }
      })
      .catch(console.error);

    // Fetch checklist progress
    fetch(`${API}/weekly-checklist-progress`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => res.json())
      .then(data => {
        const completedCount = Object.keys(data.completed_tasks || {}).filter(id => data.completed_tasks[id]).length;
        setChecklistProgress({ completed: completedCount, total: 53 }); // 53 is the total tasks (6 weeks + Monday + Tuesday)
      })
      .catch(console.error);
  }, [token, user?.store_id, user?.role]);

  // Listen for store changes from the header dropdown
  useEffect(() => {
    const handleStoreChanged = (event) => {
      const newStoreId = event.detail?.storeId;
      if (newStoreId) {
        const newStore = stores.find(s => s.id === newStoreId);
        if (newStore) {
          setCurrentStore(newStore);
          fetchDashboardData(newStoreId);
        }
      }
    };
    
    window.addEventListener('storeChanged', handleStoreChanged);
    return () => window.removeEventListener('storeChanged', handleStoreChanged);
  }, [stores]);

  // Set up countdown timer based on stocktakes
  useEffect(() => {
    const upcoming = stocktakes.find(st => new Date(st.scheduled_date) > new Date());
    if (upcoming) {
      const updateCountdown = () => {
        const now = new Date();
        const target = new Date(upcoming.scheduled_date);
        const diff = target - now;
        
        if (diff > 0) {
          const days = Math.floor(diff / (1000 * 60 * 60 * 24));
          const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const secs = Math.floor((diff % (1000 * 60)) / 1000);
          setCountdown({ days, hours, mins, secs });
        } else {
          // Stocktake date has passed
          setCountdown({ days: 0, hours: 0, mins: 0, secs: 0 });
        }
      };
      
      updateCountdown();
      const interval = setInterval(updateCountdown, 1000);
      return () => clearInterval(interval);
    } else {
      // No upcoming stocktake - reset to indicate no scheduled stocktake
      setCountdown({ days: '-', hours: '-', mins: '-', secs: '-' });
    }
  }, [stocktakes]);

  // Listen for store changes (for Network Admin store switcher)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if ((e.key === 'selectedStoreId') && (user?.role === 'network_admin' || user?.role === 'regional_manager')) {
        const newStoreId = e.newValue;
        const store = stores.find(s => s.id === newStoreId);
        if (store) {
          setCurrentStore(store);
          fetchDashboardData(store.id);
        }
      }
    };

    // Also listen for custom event for same-tab changes
    const handleStoreChange = (e) => {
      if (user?.role === 'network_admin' || user?.role === 'regional_manager') {
        const store = stores.find(s => s.id === e.detail.storeId);
        if (store) {
          setCurrentStore(store);
          fetchDashboardData(store.id);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('storeChanged', handleStoreChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('storeChanged', handleStoreChange);
    };
  }, [stores, user?.role]);

  const nextStocktake = stocktakes.find(st => new Date(st.scheduled_date) > new Date()) || stocktakes[0];
  const stocktakeDate = nextStocktake ? new Date(nextStocktake.scheduled_date) : null;
  const weeksAway = stocktakeDate ? Math.max(0, Math.ceil((stocktakeDate - new Date()) / (1000 * 60 * 60 * 24 * 7))) : null;

  // Dynamic store name - defaults to Indooroopilly SFS
  const storeName = currentStore?.name || 'Indooroopilly SFS';

  const quickNavCards = [
    {
      title: 'Pre-Stocktake Checklist',
      description: 'Preparation tasks before count day',
      link: '/pre-stocktake',
      icon: ListChecks,
      color: 'bg-slate-700',
      textColor: 'text-white'
    },
    {
      title: 'Checklist - General / Prep',
      description: '6-week preparation journey',
      link: '/checklist',
      icon: ClipboardCheck,
      color: 'bg-orange-500',
      textColor: 'text-white',
      badge: `${checklistProgress.completed}/${checklistProgress.total}`,
      live: true
    },
    {
      title: 'Roster',
      description: 'Team assignment & scheduling',
      link: '/roster-areas',
      icon: Users,
      color: 'bg-blue-500',
      textColor: 'text-white',
      badge: '0 confirmed'
    },
    {
      title: 'Verification',
      description: 'Count verification & sign-off',
      link: '/verifications',
      icon: Shield,
      color: 'bg-green-600',
      textColor: 'text-white'
    },
    {
      title: 'Admin Cut-off',
      description: 'Administration sign-off sheet',
      link: '/admin-cutoff',
      icon: FileCheck,
      color: 'bg-purple-600',
      textColor: 'text-white'
    },
    {
      title: 'Settings',
      description: 'Store & stocktake configuration',
      link: '/settings',
      icon: Activity,
      color: 'bg-slate-600',
      textColor: 'text-white'
    }
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Store Name Header */}
      <div className="text-sm text-slate-500 font-medium uppercase tracking-wider" data-testid="store-name-header">
        {storeName} Dashboard
      </div>

      {/* Welcome Banner */}
      <div className="bg-slate-800 rounded-xl p-8 text-white relative overflow-hidden" data-testid="welcome-banner">
        <div className="flex items-start justify-between relative z-10">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              WELCOME, {user?.name?.toUpperCase() || 'USER'}!
            </h1>
            <p className="text-slate-300">
              {weeksAway !== null 
                ? `You are ${weeksAway} weeks away from your stocktake`
                : 'No stocktake scheduled for this store'}
            </p>
          </div>
          <div className="text-right bg-slate-700/50 rounded-lg px-4 py-2">
            <div className="text-xs text-slate-400 mb-1">COUNT DATE</div>
            <div className="text-lg font-bold">
              {stocktakeDate 
                ? stocktakeDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                : 'TBD'}
            </div>
          </div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-700/30 to-transparent"></div>
      </div>

      {/* Stocktake Countdown */}
      <div className="bg-teal-50 border-2 border-teal-200 rounded-xl p-8" data-testid="stocktake-countdown">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-teal-700" />
            <span className="font-semibold text-slate-700">Stocktake Countdown</span>
          </div>
          <span className="text-sm text-slate-500">
            Next: {nextStocktake ? new Date(nextStocktake.scheduled_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : 'TBD'}
          </span>
        </div>
        <div className="flex items-center justify-center gap-8" data-testid="countdown-timer">
          <div className="text-center">
            <div className="text-5xl font-bold text-teal-600 mb-1">{countdown.days}</div>
            <div className="text-sm text-slate-600 uppercase tracking-wider">Days</div>
          </div>
          <div className="text-center">
            <div className="text-5xl font-bold text-teal-600 mb-1">{countdown.hours}</div>
            <div className="text-sm text-slate-600 uppercase tracking-wider">Hours</div>
          </div>
          <div className="text-center">
            <div className="text-5xl font-bold text-teal-600 mb-1">{countdown.mins}</div>
            <div className="text-sm text-slate-600 uppercase tracking-wider">Mins</div>
          </div>
          <div className="text-center">
            <div className="text-5xl font-bold text-teal-600 mb-1">{countdown.secs}</div>
            <div className="text-sm text-slate-600 uppercase tracking-wider">Secs</div>
          </div>
        </div>
      </div>

      {/* Stocktake Status Section */}
      {nextStocktake && (
        <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="planning-section">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`px-4 py-2 rounded-lg font-bold text-sm ${
                nextStocktake.status === 'in_progress' 
                  ? 'bg-orange-500 text-white' 
                  : nextStocktake.status === 'scheduled'
                  ? 'bg-teal-500 text-white'
                  : nextStocktake.status === 'completed'
                  ? 'bg-green-500 text-white'
                  : 'bg-slate-900 text-white'
              }`}>
                {nextStocktake.status === 'in_progress' 
                  ? 'IN PROGRESS' 
                  : nextStocktake.status === 'scheduled'
                  ? 'SCHEDULED'
                  : nextStocktake.status === 'completed'
                  ? 'COMPLETED'
                  : 'PLANNING'}
              </div>
              <div>
                <div className="font-semibold text-slate-900">{nextStocktake.store_name}</div>
                {nextStocktake.status === 'in_progress' && (
                  <div className="text-sm text-orange-600 font-medium">Count is currently active</div>
                )}
              </div>
            </div>
            <span className="text-sm text-slate-500">
              {nextStocktake.status === 'in_progress' ? 'Live' : 'Just now'}
            </span>
          </div>
        </div>
      )}

      {/* Quick Navigation */}
      <div>
        <h2 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-4">Quick Navigation</h2>
        <div className="space-y-4">
          {quickNavCards.map((card) => {
            const Icon = card.icon;
            return (
              <Link
                key={card.title}
                to={card.link}
                className={`${card.color} ${card.textColor} rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200 group relative overflow-hidden flex items-center justify-between`}
                data-testid={`nav-card-${card.title.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="flex items-center gap-4 relative z-10">
                  <div className="p-3 rounded-lg bg-white/10 backdrop-blur-sm">
                    <Icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-1">{card.title}</h3>
                    <p className="text-sm opacity-90">{card.description}</p>
                  </div>
                </div>
                {card.badge && (
                  <div className={`text-lg font-bold bg-white/20 px-4 py-2 rounded-full relative z-10 ${card.live ? 'animate-pulse' : ''}`}>
                    {card.badge}
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Run Setup Wizard */}
      <button
        className="w-full bg-gradient-to-r from-teal-600 to-teal-500 rounded-xl p-6 text-white shadow-lg hover:shadow-xl transition-all text-center group"
        data-testid="run-setup-wizard-button"
      >
        <div className="flex items-center justify-center gap-3">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span className="text-xl font-bold">Run Setup Wizard</span>
        </div>
      </button>
    </div>
  );
};

export default Dashboard;
